import { useEffect, useState } from "react";
import { BASE_URL } from "../apiConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

const InsightBlock = ({
  description,
  summary,
}: {
  description?: string;
  summary?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <p className="text-sm text-slate-600">{description}</p>
    {summary ? (
      <div className="mt-3 border-l-4 border-slate-400 pl-3">
        <p className="text-xs font-medium text-slate-700">Key insight</p>
        <p className="text-sm text-slate-700">{summary}</p>
      </div>
    ) : null}
  </div>
);

const Card = ({
  title,
  subtitle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        ) : null}
      </div>
      {headerRight}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

type InventoryInsightData = {
  description?: string;
  summary?: string;
  data?: any;
  confidence?: string;
  error?: string;
};

export default function InventoryTab() {
  // const [frequentStockouts, setFrequentStockouts] = useState<InventoryInsightData | null>(null);
  const [overstockedSkus, setOverstockedSkus] = useState<InventoryInsightData | null>(null);
  const [replenishmentGaps, setReplenishmentGaps] = useState<InventoryInsightData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchInventoryInsights() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/inventory-insights`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        // setFrequentStockouts(data.frequent_stockouts);
        setOverstockedSkus(data.overstocked_skus);
        setReplenishmentGaps(data.replenishment_gaps);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchInventoryInsights();
  }, []);

  // Formatters
  const formatOverstockedSkus = (data: any) =>
    Array.isArray(data?.data)
      ? data.data.map((item: any) => ({
          store: item.store_id,
          sku: item.sku_id,
          on_hand: Number(item.on_hand ?? 0),
          avg_daily_qty: Number((item.avg_daily_qty ?? 0).toFixed?.(2) ?? item.avg_daily_qty ?? 0),
        }))
      : [];

  const formatReplenishmentGaps = (data: any) =>
    Array.isArray(data?.data)
      ? data.data.map((item: any) => ({
          store: item.store_id,
          sku: item.sku_id,
          avg_7d_sales: Number((item.avg_7d_sales ?? 0).toFixed?.(2) ?? item.avg_7d_sales ?? 0),
          on_hand: Number(item.on_hand ?? 0),
        }))
      : [];

  // const formatStockouts = (data: any) =>
  //   Array.isArray(data?.data)
  //     ? data.data.map((item: any, idx: number) => ({
  //         key: `${item.store_id ?? "store"}-${item.sku_id ?? idx}`,
  //         store: item.store_id ?? "—",
  //         sku: item.sku_id ?? "—",
  //         days: item.days_out ?? item.stockout_days ?? item.count ?? 0,
  //       }))
  //     : [];

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="h-6 w-56 animate-pulse rounded bg-slate-200" />
        <div className="mt-6 h-64 w-full animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }
  if (error) return <div className="p-6 md:p-8 text-red-600">Error: {error}</div>;

  const EmptyState = ({ label }: { label: string }) => (
    <div className="flex h-[260px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500">
      {label}
    </div>
  );

  return (
    <div className="p-6 md:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Inventory Insights</h1>
      </div>

      {/* Frequent Stockouts card removed */}

      {/* Two-column charts */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Overstocked SKUs */}
        <Card title="Overstocked SKUs" subtitle="On-hand vs. average daily sales">
          <div className="mb-5">
            <InsightBlock
              description={overstockedSkus?.description}
              summary={overstockedSkus?.summary}
            />
          </div>
          <div className="h-[320px] w-full">
            {formatOverstockedSkus(overstockedSkus).length === 0 ? (
              <EmptyState label="No overstocked SKU data." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={formatOverstockedSkus(overstockedSkus)}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  barCategoryGap="25%"
                >
                  <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                  <XAxis dataKey="sku" tick={{ fill: "#6B7280", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="on_hand" name="On Hand" fill="#111827" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="avg_daily_qty" name="Avg Daily Qty" fill="#9CA3AF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Replenishment Gaps */}
        <Card title="Replenishment Gaps" subtitle="Avg 7d sales vs. on-hand">
          <div className="mb-5">
            <InsightBlock
              description={replenishmentGaps?.description}
              summary={replenishmentGaps?.summary}
            />
          </div>
          <div className="h-[320px] w-full">
            {formatReplenishmentGaps(replenishmentGaps).length === 0 ? (
              <EmptyState label="No replenishment data." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={formatReplenishmentGaps(replenishmentGaps)}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="avg7dSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="onHand" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                  <XAxis dataKey="sku" tick={{ fill: "#6B7280", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px'
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="avg_7d_sales"
                    name="Avg 7d Sales"
                    stroke="#3B82F6"
                    fillOpacity={1}
                    fill="url(#avg7dSales)"
                  />
                  <Area
                    type="monotone"
                    dataKey="on_hand"
                    name="On Hand"
                    stroke="#EF4444"
                    fillOpacity={1}
                    fill="url(#onHand)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}